/**
 * wayai init [--org <uuid>] — scope this repo to an organization.
 *
 * Interactive mode: prompts user to pick an org (or auto-selects if only one).
 * Direct mode:     --org <uuid> sets the org without prompting.
 *
 * The binding goes into the workspace manifest `wayai-ws/wayai.yaml`, beside the
 * repo's routing defaults — ONE committed file, not two. The root `.wayai.yaml`
 * that used to hold it is read as a deprecated fallback and is never written
 * here; `wayai migrate` copies an existing one forward (`lib/repo-config.ts`).
 *
 * Hubs self-identify via `hub.yaml` inside their folder; the manifest carries
 * only org context, so that push/pull know which org to talk to.
 */

import { mkdirSync } from 'node:fs';
import path from 'node:path';
import { requireAuth } from '../lib/auth.js';
import { ApiClient } from '../lib/api-client.js';
import { readRepoConfig, writeRepoConfig } from '../lib/repo-config.js';
import { findGitRoot } from '../lib/workspace.js';
import { resolveLayout, isDirectory } from '../lib/layout.js';
import { UUID_RE, prompt } from '../lib/utils.js';
import { SKILL_INSTALL_COMMAND } from '@wayai/core/constants';

// Single source of truth in @wayai/core (the `mkdir -p .claude` prefix is
// load-bearing — see there).
export const SKILL_INSTALL_CMD = SKILL_INSTALL_COMMAND;

/**
 * Create the hubs dir for the active layout. Dir-only — the CLI seeds no
 * repo-root agent-context files at all; the only `AGENTS.md` it writes is the
 * per-hub memory scaffold inside a hub folder (yaml-writer.ts).
 *
 * Returns the created hubs dir (relative path), or null if it already existed.
 */
export function ensureHubsDir(gitRoot: string): string | null {
  // Use the active layout (`wayai-ws/hubs/` on a fresh repo; the legacy
  // `workspace/` if this repo predates migration).
  const hubsDir = resolveLayout(gitRoot).hubsDir;
  if (isDirectory(hubsDir)) return null;
  mkdirSync(hubsDir, { recursive: true });
  return `${path.relative(gitRoot, hubsDir)}/`;
}

export function parseArgs(args: string[]): { orgId: string | undefined; autoConfirm: boolean } {
  let orgId: string | undefined;
  let autoConfirm = false;
  const orgIdx = args.indexOf('--org');
  if (orgIdx !== -1 && args[orgIdx + 1]) {
    orgId = args[orgIdx + 1];
  }
  for (const arg of args) {
    if (arg === '--yes' || arg === '-y') {
      autoConfirm = true;
    }
  }
  return { orgId, autoConfirm };
}

export async function initCommand(args: string[]): Promise<void> {
  const { orgId: directOrgId, autoConfirm } = parseArgs(args);

  // Warn if this repo is already scoped — whichever file supplied the binding.
  const existing = readRepoConfig();
  if (existing) {
    const scopeLabel = existing.organization_name
      ? `${existing.organization_name} (${existing.organization_id})`
      : existing.organization_id;
    console.log(`Current scope: ${scopeLabel}`);
    if (!autoConfirm) {
      const answer = await prompt('Overwrite? [y/N]: ');
      if (answer.toLowerCase() !== 'y') {
        console.log('Cancelled.');
        return;
      }
    }
  }

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  if (directOrgId) {
    await initDirect(client, directOrgId);
  } else {
    await initInteractive(client, autoConfirm);
  }

  // Create the hubs dir. Nothing else: `init` writes no agent-context files at
  // the repo root — the WayAI skill is the agent's entry point.
  const gitRoot = findGitRoot();
  if (gitRoot) {
    const createdDir = ensureHubsDir(gitRoot);
    if (createdDir) {
      console.log(`Created ${createdDir}`);
    }
  }

  console.log(
    `Run \`${SKILL_INSTALL_CMD}\` to install the WayAI skill in your AI harness, then \`wayai pull\` to fetch hub configuration.`,
  );
}

async function initDirect(client: ApiClient, orgId: string): Promise<void> {
  if (!UUID_RE.test(orgId)) {
    console.error('Invalid organization ID. Expected a UUID.');
    process.exit(1);
  }

  console.log('Looking up organization...');
  const { organizations } = await client.organizations();
  const match = organizations.find((o) => o.id === orgId);

  if (!match) {
    console.error(`Organization ${orgId} not found among the orgs you can access.`);
    process.exit(1);
  }

  // Refuses — without writing — when the deprecated root binding names a
  // different organization: the disagreement is between the two files, so
  // writing here would break a repo that was working rather than re-scope it.
  const configPath = writeRepoConfig({
    organization_id: match.id,
    organization_name: match.name,
  });

  console.log(`Found: ${match.name}`);
  console.log(`\nWrote ${configPath}`);
}

async function initInteractive(client: ApiClient, autoConfirm: boolean): Promise<void> {
  console.log('Fetching organizations...');
  let organizations = (await client.organizations()).organizations;

  if (organizations.length === 0) {
    // No org yet — create one inline rather than bouncing to the web UI. In
    // non-interactive mode (-y / CI) there's no one to prompt, so direct the
    // caller to the scriptable primitive instead of hanging on stdin.
    if (autoConfirm) {
      console.error('No organizations found. Create one with `wayai org create "<name>"`, then re-run `wayai init`.');
      process.exit(1);
    }
    console.log('\nNo organizations found.');
    const name = (await prompt('Name your first organization (blank to cancel): ')).trim();
    if (!name) {
      console.error('Cancelled. Create one later with `wayai org create "<name>"`.');
      process.exit(1);
    }
    const { organization } = await client.createOrganization(name);
    console.log(`Created organization: ${organization.name} (${organization.id})`);
    if (organization.region && !organization.region_explicit) {
      console.log(`  Region: ${organization.region} (closest to you — re-create with \`wayai org create --region\` to change)`);
    }
    organizations = [{ id: organization.id, name: organization.name }];
  }

  let selectedOrg: { id: string; name: string };

  if (organizations.length === 1) {
    selectedOrg = organizations[0];
    console.log(`Organization: ${selectedOrg.name}`);
  } else {
    console.log('\nOrganizations:');
    for (let i = 0; i < organizations.length; i++) {
      console.log(`  ${i + 1}. ${organizations[i].name}`);
    }
    const orgAnswer = await prompt(`\nSelect organization [1-${organizations.length}]: `);
    const orgIdx = parseInt(orgAnswer, 10) - 1;
    if (isNaN(orgIdx) || orgIdx < 0 || orgIdx >= organizations.length) {
      console.error('Invalid selection.');
      process.exit(1);
    }
    selectedOrg = organizations[orgIdx];
  }

  const configPath = writeRepoConfig({
    organization_id: selectedOrg.id,
    organization_name: selectedOrg.name,
  });

  console.log(`\nWrote ${configPath}`);
}
